import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Clock, Calendar, ArrowRight } from 'lucide-react';
import { BASE_URL } from '../../apis/api';
import newsBg from '../../images/cover/news-bg.jpg';
import RouteConstants from '../../constant/Routeconstant';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';

const FeaturedNews = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [transitionDirection, setTransitionDirection] = useState('right');
    const intervalRef = useRef(null);
    const thumbnailContainerRef = useRef(null);

    const navigate = useNavigate();

    // Fetch news data
    useEffect(() => {
        const fetchNews = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${BASE_URL}/news/list-featurednews`);
                if (!response.ok) {
                    throw new Error('Failed to fetch news');
                }
                const data = await response.json();
                setNews(data?.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
    }, []);

    // Auto-play functionality
    useEffect(() => {
        if (isAutoPlaying && news.length > 0) {
            intervalRef.current = setInterval(() => {
                setTransitionDirection('right');
                setCurrentIndex((prevIndex) => (prevIndex + 1) % news.length);
                scrollThumbnailToCenter((currentIndex + 1) % news.length);
            }, 4000);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isAutoPlaying, news.length, currentIndex]);

    // Scroll thumbnail to center when currentIndex changes
    useEffect(() => {
        scrollThumbnailToCenter(currentIndex);
    }, [currentIndex]);

    const scrollThumbnailToCenter = (index) => {
        if (thumbnailContainerRef.current) {
            const container = thumbnailContainerRef.current;
            const item = container.children[index];
            if (item) {
                const containerWidth = container.offsetWidth;
                const itemLeft = item.offsetLeft;
                const itemWidth = item.offsetWidth;
                const scrollTo = itemLeft - (containerWidth / 2) + (itemWidth / 2);

                container.scrollTo({
                    left: scrollTo,
                    behavior: 'smooth'
                });
            }
        }
    };

    // Navigation functions with directional control
    const goToSlide = (index) => {
        const direction = index > currentIndex ? 'right' : 'left';
        setTransitionDirection(direction);
        setCurrentIndex(index);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 5000);
    };

    const nextSlide = () => {
        if (currentIndex === news.length - 1) {
            // Going from last to first - animate right
            setTransitionDirection('right');
            setCurrentIndex(0);
        } else {
            setTransitionDirection('right');
            setCurrentIndex(currentIndex + 1);
        }
        scrollThumbnailToCenter((currentIndex + 1) % news.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 5000);
    };

    const prevSlide = () => {
        if (currentIndex === 0) {
            // Going from first to last - animate left
            setTransitionDirection('left');
            setCurrentIndex(news.length - 1);
        } else {
            setTransitionDirection('left');
            setCurrentIndex(currentIndex - 1);
        }
        scrollThumbnailToCenter((currentIndex - 1 + news.length) % news.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 5000);
    };

    // Utility functions
    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return 'Recent';
        }
    };

    const handleViewAll = () => {
        navigate(RouteConstants.NEWSLISTILFK);
    };

    const decodeHtml = (html) => {
        const textArea = document.createElement('textarea');
        textArea.innerHTML = html;
        return textArea.value;
    };

    if (loading) {
        return (
            <Loader />
        );
    }

    if (news.length === 0) {
        return (
            <div className="min-h-96 flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50">
                <div className="text-center text-black">
                    <p className="text-lg font-semibold mb-2">Unable to load news</p>
                    <p className="text-sm">Please try again later</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 py-16 news"
            style={{
                backgroundImage: `url(${newsBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12 md:mb-16">
                    <div className="flex items-center justify-center mb-4 sm:mb-6">
                        <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                        <h4 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mx-4 sm:mx-6 md:mx-8 relative">
                            <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-black">
                                FEATURED NEWS
                            </span>
                            <div className="absolute -bottom-2 sm:-bottom-2 left-1/2 transform -translate-x-1/2 w-10 sm:w-12 md:w-16 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
                        </h4>
                        <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                    </div>
                    <p className="text-black text-base sm:text-lg md:text-xl max-w-2xl sm:max-w-3xl mx-auto leading-relaxed">
                        Stay updated with the latest stories and developments
                    </p>
                </div>

                {/* Main Carousel */}
                <div className="relative px-13 topContent">
                    <div className="overflow-hidden rounded-2xl shadow-2xl max-h-[400px] min-h-[150px]: bg-white">
                        <div
                            className="flex transition-transform duration-700 ease-in-out"
                            style={{
                                transform: `translateX(-${currentIndex * 100}%)`,
                                transitionTimingFunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)'
                            }}
                        >
                            {news?.map((item, index) => (
                                <div key={item.newsid || item.id || index} className="w-full flex-shrink-0 newsView">
                                    <div
                                    onClick={() =>navigate(`/newsviewsite/${item?.news?.id}/${item?.news?.shortdescription}`)}
                                     className="grid lg:grid-cols-2 gap-0 cursor-pointer">
                                        {/* Image Section */}
                                       <div className="relative overflow-hidden h-80 p-2 rounded-lg">
                                            {/* Blurred background image */}
                                            <img
                                                src={item?.news?.image ? `${BASE_URL}/${item?.news?.image}` : '/placeholder-image.jpg'}
                                                alt=""
                                                className="absolute inset-0 w-full h-full object-cover blur-md scale-110"
                                            />
                                            
                                            {/* Main image centered */}
                                            <img
                                                src={item?.news?.image ? `${BASE_URL}/${item?.news?.image}` : '/placeholder-image.jpg'}
                                                alt={item?.news?.shortdescription}
                                                className="relative z-10 w-full h-full object-contain rounded-lg transition-transform duration-700 hover:scale-105"
                                            />
                                            
                                            <div className="absolute inset-2 bg-gradient-to-t from-black/50 via-transparent to-transparent lg:hidden rounded-lg z-20">
                                                <div className="absolute bottom-4 left-4 right-4">
                                                    <div className="flex items-center gap-2 text-white/90 text-sm mb-2">
                                                        <Calendar className="w-4 h-4" />
                                                        {formatDate(item?.news?.createdAt)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Content Section */}
                                        <div className="p-8 lg:p-12 flex flex-col justify-center topContent h-80">
                                            <div className="hidden lg:flex items-center gap-2 text-gray-700 mb-4 text-[16px]">
                                                <Calendar className="w-4 h-4" />
                                                {formatDate(item?.news?.createdAt)}
                                                {/* <span className="mx-2">•</span>
                                                <Clock className="w-4 h-4" />
                                                2 min read */}
                                            </div>

                                            <h3 className="text-2xl lg:text-3xl font-bold text-black mb-4 leading-tight text-[17px]">
                                                {item?.news?.shortdescription}
                                            </h3>

                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: decodeHtml(item?.news?.maindescription)
                                                }}
                                                 className="text-gray-800 text-[15px] leading-snug line-clamp-2 overflow-hidden prose max-w-none"
                                            />
                                            <button
                                                className="inline-flex w-[140px] mt-5 items-center justify-center text-black bg-[#efc847] text-[15px] font-normal px-4 py-3 rounded-full shadow-md hover:shadow-lg transition"
                                                onClick={() =>navigate(`/newsviewsite/${item?.news?.id}/${item?.news?.shortdescription}`)}
                                            >
                                                Read Full Story
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Navigation controls */}
                <div className="flex items-center justify-center mt-6 gap-1 bottumContent">
                    <button
                        onClick={prevSlide}
                        className="bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 arrowBtn"
                        aria-label="Previous slide"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>

                    {/* Thumbnail Navigation */}
                    <div className="flex-1 overflow-hidden relative">
    <div
        className="flex overflow-x-auto gap-4 pb-4 pl-5 scrollbar-hide bottumContainer"
        ref={thumbnailContainerRef}
        style={{
            scrollBehavior: 'smooth',
            scrollSnapType: 'x mandatory'
        }}
    >
        {news.map((item, index) => (
            <button
                key={item.newsid || item.id || index}
                onClick={() => goToSlide(index)}
                className={`slider-thumb flex flex-col flex-shrink-0 w-48 rounded-xl transition-all duration-300 overflow-hidden ${index === currentIndex
                    ? 'bg-blue-50 border-2 border-blue-200 shadow-md scale-105'
                    : 'bg-white hover:bg-gray-50 border border-gray-200 hover:shadow-md'
                    }`}
                style={{
                    scrollSnapAlign: 'center'
                }}
            >
                <div className="relative w-full h-40 overflow-hidden rounded-t-xl">
                    {/* Blurred background image */}
                    <img
                        src={item?.news?.image ? `${BASE_URL || 'http://localhost:5000'}/${item?.news?.image}` : ''}
                        alt=""
                        className="absolute inset-0 w-full h-full object-fill blur-sm scale-110"
                    />
                    
                    {/* Main image centered */}
                    <img
                        src={item?.news?.image ? `${BASE_URL || 'http://localhost:5000'}/${item?.news?.image}` : ''}
                        alt={item.shortdescription}
                        className="relative z-10 w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                    />
                </div>
                
                <div className="p-3 flex flex-col flex-grow">
                    <h4 className="font-normal text-gray-900 line-clamp-2 text-[13px] leading-tight mb-2">
                        {item?.news?.shortdescription}
                    </h4>
                    <p className="font-normal text-[11px] text-gray-600 mt-auto">
                        {formatDate(item?.news?.createdAt)}
                    </p>
                </div>
            </button>
        ))}
    </div>
</div>

                    <button
                        onClick={nextSlide}
                        className="bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 arrowBtn"
                        aria-label="Next slide"
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>
                </div>

                <div className="text-center mt-8 sm:mt-10 md:mt-12">
                    <button
                        onClick={handleViewAll}
                        className="group relative inline-flex items-center px-6 py-2 sm:px-8 sm:py-3 md:px-10 md:py-4 
                                  bg-black text-white font-normal rounded-full 
                                    hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-400 hover:text-gray-900 
                                    transition-all duration-500 transform hover:scale-105 hover:shadow-xl 
                                    border-2 border-transparent hover:border-yellow-300 text-sm sm:text-base md:text-lg viewMoreBtn"
                    >
                        <span className="relative z-10 text-sm">More News</span>
                        <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ml-2 sm:ml-3 transform group-hover:translate-x-1 transition-transform duration-300 relative z-10"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </button>

                </div>
            </div>
        </div>
    );
};

export default FeaturedNews;